// Pure helpers — no Supabase calls live here. Imports + writes happen in
// sendMessage / subscribeRealtime / createChannel / markRead.
import { localDate } from '@symbo-ls/workspace-shared/functions/dateUtils.js'
import { firstName as _firstName } from '@symbo-ls/workspace-shared/functions/peopleUtils.js'
import { stripWorkspacePrefix } from '@symbo-ls/workspace-shared/functions/workspacePrefix.js'
import { wsIdOf } from './sameWorkspaceWriter.js'
// The chat twin of the shell's peopleByEmail: also maps every person a chat
// member row names by user_id (a non-manager's /people rows carry no peer
// email — see chatPeople.js).
import { chatPeopleByEmail as _peopleByEmail } from './chatPeople.js'

export const rowToMessage = (r) => ({
  id: r.id,
  channelId: r.channel_id,
  authorEmail: r.author_email,
  body: r.body,
  attachments: r.attachments || null,
  replyTo: r.reply_to,
  threadRoot: r.thread_root,
  threadCount: r.thread_count || 0,
  editedAt: r.edited_at,
  deleted: !!r.deleted,
  createdAt: r.created_at,
  // Soft column — `created_at` parsed to epoch ms ONCE, here, so the sort key
  // is a plain number. sortMessagesByTime runs on every setChannelMessages
  // write and would otherwise build two Dates per comparison. A missing or
  // unparseable `created_at` reads as 0 (sorts first), never NaN.
  createdAtTimestamp: Date.parse(r.created_at) || 0,
  reactions: r.reactions
})

// Full "Weekday D Mon, HH:MM" label for the message-bubble hover tooltip
// (CHAT-POLISH-1, tickets/sonnet.md) — hovering a message only ever showed
// the bare clock time (`02:45`), so finding an older message's calendar day
// meant scrolling up to its day separator. Reuses the exact weekday+month+day
// shape dateUtils.js's dayLabel() already renders for those separators —
// deliberately never collapsed to "Today"/"Yesterday" here, since showing
// the real calendar day is the entire point of the tooltip — plus the same
// HH:MM clock format the visible hover time already renders with
// (dateUtils.js's clockTime()).
// `function`, not an arrow: `el.call('hoverTimestamp', ts)` binds the element,
// and the weekday + month names have to read in the SESSION language (shared
// functions/localizedDate.js; Chrome has no Georgian names of its own).
export function hoverTimestamp(ts) {
  const el = this && typeof this.call === 'function' ? this : null
  if (!ts) return ''
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  const dayOpts = { weekday: 'long', day: 'numeric', month: 'short' }
  const timeOpts = { hour: '2-digit', minute: '2-digit' }
  // Only a non-empty STRING counts, so a registry without localizedDate keeps
  // the old browser-locale output.
  const dayOut = el ? el.call('localizedDate', d, dayOpts) : ''
  const timeOut = el ? el.call('localizedDate', d, timeOpts) : ''
  const day =
    typeof dayOut === 'string' && dayOut ? dayOut : d.toLocaleDateString(undefined, dayOpts)
  const time =
    typeof timeOut === 'string' && timeOut ? timeOut : d.toLocaleTimeString(undefined, timeOpts)
  return `${day}, ${time}`
}

// Canonical email form for identity comparisons. Chat rows carry
// mixed-case emails (Mongo-sourced people like "Nika@Symbols.app" vs the
// always-lowercase auth rows — see createChannel's lowercasing note), so
// every membership/identity comparison must go through this. Non-strings
// resolve to null so `filter(Boolean)` drops them.
export const normEmail = (e) => (typeof e === 'string' ? e.toLowerCase().trim() : null)

// Distinct non-self participant emails, normalized. Single source for
// "which people are actually in this conversation" when composing a new
// group (the sidebar drag-to-group drop). A person represented twice —
// case-variant membership rows, or the same person reached through two
// duplicate DM rows — collapses to ONE entry, and dropping a person onto
// themselves composes to fewer than two others (callers no-op on that)
// instead of minting a bogus group of one duplicated person.
export const distinctOthers = (emails, me) => {
  const meNorm = normEmail(me)
  const out = []
  const seen = new Set()
  for (const e of emails || []) {
    const n = normEmail(e)
    if (!n || n === meNorm || seen.has(n)) continue
    seen.add(n)
    out.push(n)
  }
  return out
}

// ── Fetch-vs-local merge (bootstrap no-clobber) ─────────────────────────────
// A channels/members fetch snapshot starts BEFORE any concurrent local write
// (createChannel / messageUser append the new DM + membership optimistically),
// so REPLACING state with the snapshot silently deletes rows created while the
// fetch was in flight — the race that reset activeChannelId back to null and
// left "avatar → Chat" stranded on the bare list. Fetched rows win on identity
// collisions (server truth); local-only rows survive the landing.
export const mergeChannelLists = (fetched, local) => {
  const out = [...(Array.isArray(fetched) ? fetched : [])]
  const ids = new Set(out.map((c) => c && c.id).filter(Boolean))
  for (const c of local || []) {
    if (!c || !c.id || ids.has(c.id)) continue
    ids.add(c.id)
    out.push(c)
  }
  return out
}

export const mergeMemberLists = (fetched, local) => {
  const out = [...(Array.isArray(fetched) ? fetched : [])]
  const key = (m) => `${m && m.channel_id}\x00${normEmail(m && m.member_email) || ''}`
  const seen = new Set(out.map(key))
  for (const m of local || []) {
    if (!m || !m.channel_id || !m.member_email) continue
    const k = key(m)
    if (seen.has(k)) continue
    seen.add(k)
    out.push(m)
  }
  return out
}

export const rowToChannel = (r) => ({
  id: r.id,
  kind: r.kind,
  name: r.name,
  topic: r.topic,
  // Carry workspace_id through so consumers can strictly scope the
  // channel list to the active workspace. The chat_channels RLS
  // predicate is `(workspace_id = jwt_active_workspace()) OR
  // jwt_is_channel_member(id)` — that second clause means a user who
  // was added as a member of a Symbols-org channel still sees it when
  // their active org is "Personal". Surface workspace_id so the
  // workspace UI can filter strictly: only show channels that BELONG
  // to the active workspace, matching the user's mental model.
  workspaceId: r.workspace_id,
  // CHAT-AGENT-CONNECT (migration 0147) — when set, this channel is wired to
  // an assistant agent (e.g. simone-agent@symbols.app). The server-side
  // ChatAgentSubscriber replies in-thread on @simone/@simona mention or
  // thread-reply; the UI uses it to surface the agent as a channel
  // participant (mention highlight + author name).
  agentEmail: r.agent_email || null,
  isPrivate: !!r.is_private,
  isArchived: !!r.is_archived,
  createdBy: r.created_by,
  createdAt: r.created_at,
  updatedAt: r.updated_at
})

// Filter a list of channels to only those owned by the active
// workspace. Used by the chat page + onWorkspaceChange refetch so org
// switch in the workspace UI hides cross-workspace channels even when
// the server-side RLS lets them through (via the `is_channel_member`
// disjunct). Channels with no workspaceId (legacy rows pre-RLS-0141)
// pass through unfiltered — better to over-show than to hide active
// data behind a column that may not be populated.
export const filterChannelsToActiveWorkspace = (channels, activeWorkspaceId) => {
  const list = Array.isArray(channels) ? channels : []
  if (!activeWorkspaceId) return list
  return list.filter((c) => !c.workspaceId || c.workspaceId === activeWorkspaceId)
}

// Should this channel appear in the sidebar's CHANNELS rail?
//
// Mirrors the SERVER's access rule rather than contradicting it. `canAccessChannel`
// is `workspace_id = jwt_active_workspace() OR jwt_is_channel_member(id)`, so a
// PUBLIC channel passes on the workspace leg with no member row at all. The rail
// used to require a member row for everything, which made public workspace
// channels (seeded #general/#team, and any channel created without seeding
// members) reachable by URL and returned by the list route, yet invisible in the
// sidebar to the very members they exist for.
//
// ⚠️ PRIVATE channels still require membership — the server drops the workspace
// leg for them, so relaxing it here would show a private channel to a non-member.
//
// ⚠️ The non-member case requires an EXACT workspace match. Do not lean on
// `filterChannelsToActiveWorkspace` for this: it passes any channel whose
// `workspaceId` is falsy, so an un-normalized or workspace-less row would
// otherwise surface in EVERY workspace — a cross-tenant leak in the rail.
// (See CASE-CONVENTION-MISMATCH-SWEEP-1: raw rows carry `workspace_id`, only
// `rowToChannel` output carries `workspaceId`.)
export const isChannelVisibleInRail = (channel, { isMember, activeWorkspaceId } = {}) => {
  if (!channel || channel.kind !== 'channel') return false
  if (isMember) return true
  if (channel.isPrivate) return false
  if (!activeWorkspaceId) return false
  return String(channel.workspaceId || '') === String(activeWorkspaceId)
}

// Count unread messages for the current viewer in a single channel.
// `messages` is the channel's message array, `lastReadAt` is the viewer's
// last_read_at timestamp on chat_channel_members. Messages authored by the
// viewer don't count as unread.
export const unreadCount = (messages, lastReadAt, currentUserEmail) => {
  if (!Array.isArray(messages) || !messages.length) return 0
  const cutoff = lastReadAt ? new Date(lastReadAt).getTime() : 0
  let n = 0
  for (const m of messages) {
    if (m.deleted) continue
    if (m.authorEmail === currentUserEmail) continue
    const t = m.createdAt ? new Date(m.createdAt).getTime() : 0
    if (t > cutoff) n++
  }
  return n
}

// Per-(channelMembers identity, me) cache of the set of muted channel ids.
// Invalidates automatically when channelMembers is replaced (which happens
// on every realtime upsert). Drastically cuts the cost of `_isMuted` calls
// on every sidebar row during render.
const _mutedCache = new WeakMap()
export const mutedChannels = (channelMembers, me) => {
  if (!channelMembers || !me) return _EMPTY_SET
  let cached = _mutedCache.get(channelMembers)
  if (cached && cached._me === me) return cached
  const set = new Set()
  for (const m of channelMembers) {
    if (m.member_email === me && m.muted) set.add(m.channel_id)
  }
  set._me = me
  _mutedCache.set(channelMembers, set)
  return set
}
const _EMPTY_SET = new Set()

// DM channels have no name — synthesise one from the members. Pass
// `firstOnly: true` to drop surnames (used by the sidebar group-chat list
// to save horizontal space).
export const channelDisplayName = (
  channel,
  members,
  peopleByEmail,
  currentUserEmail,
  opts = {}
) => {
  if (!channel) return ''
  // Returns a real name, or a polyglot KEY for a nameless conversation. Every
  // render site resolves the WHOLE returned string through polyglot, and
  // translate() passes a real name (and a '# ' prefix) through unchanged.
  if (channel.kind === 'channel') return channel.name || 'chat.channel.channelFallback'
  // Dedupe participants case-insensitively — legacy membership rows can hold
  // the same person under two casings, which rendered titles like
  // "Viktor, Viktor" (one human, twice). Same normalization the composer
  // paths use (normEmail), applied at display so polluted rows read right.
  const meNorm = normEmail(currentUserEmail)
  const seenOthers = new Set()
  const others = (members || [])
    .filter((m) => {
      if (m.channel_id !== channel.id) return false
      const n = normEmail(m.member_email)
      if (!n || n === meNorm || seenOthers.has(n)) return false
      seenOthers.add(n)
      return true
    })
    .map((m) => {
      const full = peopleByEmail[m.member_email]?.name || m.member_email
      return opts.firstOnly ? _firstName(full) : full
    })
  if (channel.kind === 'dm') return others[0] || 'chat.channel.directMessage'
  return others.join(', ') || 'chat.channel.groupDm'
}

// Resolve ChatHeader's channel title. Shared by the visible `text` and the
// hover `title` tooltip so the full name stays available when the visible
// title truncates. Moved here from a `components/Main.js` module-scope
// closure (`_headerTitle`) — a Rule 33 violation (module-level helper
// referenced from an exported factory): the workspace-ui catalog re-hydrates
// component factories from a stringified form, and a bare closure reference
// resolves to `__scope._headerTitle is not a function` there, which the
// factory error path swallows as empty text — LeftGroup renders 0×0, and
// PageHeader's own solid `background: 'document'` box (sized by its
// `minHeight`, taller than the centered TabSwitcher pill) is left showing
// above the pill with nothing to explain it: exactly CAT-5 item 6's
// "unstyled black rectangle floating above the tab bar" (confirmed live
// against the deployed catalog — `[data-key="LeftGroup"]` measured 0×0,
// `_headerTitle` threw before ever reaching any hover-actions code, ruling
// out the setupHoverActions cross-cell-fallback lead this ticket suspected).
// Answers a bundle KEY or a channel's own name; the caller resolves either
// through polyglot, which echoes what is not a key. (`Select a channel` was an
// English literal here and read English as the header's tooltip.)
export const chatHeaderTitle = (root) => {
  if (!root) return 'chat.header.selectChannel'
  const activeId = root.activeChannelId
  if (!activeId) return 'chat.header.selectChannel'
  const ch = (root.channels || []).find((c) => c.id === activeId)
  if (!ch) return 'chat.channel.channelFallback'
  const prefix = ch.kind === 'channel' ? '# ' : ''
  return (
    prefix +
    channelDisplayName(ch, root.channelMembers || [], _peopleByEmail(root), root.currentUserEmail, {
      firstOnly: ch.kind === 'group_dm'
    })
  )
}

// Is this message authored by the current viewer? Shared by MessageList.js's
// ChatMessageRow (bubble alignment/background/round, data-mine attr) and
// ThreadPanel.js's Parent/ChatThreadReplyRow (identical styling contract for
// thread replies). Moved out of BOTH files' module-scope `_isMine` closures —
// Rule 33 ("No module-level variables/helpers/closures... closures don't
// survive frank serialization"): the workspace-ui catalog re-hydrates each
// component factory from a stringified form, and a bare reference to a name
// absent from the `__scope` stand-in throws `__scope._isMine is not a
// function` there — the same class already fixed for ChatHeader's
// `_headerTitle` (`chatHeaderTitle`, above). Both call sites now go through
// `el.call('isMine', s)`.
export const isMine = (s) =>
  !!(s && s.root && s.authorEmail && s.authorEmail === s.root.currentUserEmail)

// Messages-per-page window for MessageList.js's LoadMore pagination. Kept as
// a registered zero-arg function (not a bare module-scope constant read
// inside `LoadMore.text`/`.onClick`) for the identical Rule 33 reason as
// `isMine` above — a module-scope const referenced from a reactive prop
// factory is the same unresolvable-at-re-hydration shape as a closure.
export const chatPageSize = () => 50

// Visible-count window for a channel's message pagination
// (`root.chatVisibleCounts`). Moved out of MessageList.js's module-scope
// `_getVisible` closure — Rule 33, same class as `isMine` above.
export const getVisible = (root, channelId) =>
  (root && root.chatVisibleCounts && root.chatVisibleCounts[channelId]) || chatPageSize()

// Resolve a reply's original message. Returns null when the original message
// isn't in the loaded window. Moved out of MessageList.js's module-scope
// `_replyTarget` closure — Rule 33, same class as `isMine` above. The
// original signature took an unused `el` param (kept here as `s`-only since
// callers already have `el` for `el.call`).
export const replyTarget = (s) => {
  if (!s || !s.replyTo || !s.root) return null
  const cid = s.channelId || s.root.activeChannelId
  const list = (s.root.messagesByChannel || {})[cid] || []
  return list.find((m) => m.id === s.replyTo) || null
}

// Live reply count for a parent message's inline "N replies" pill
// (MessageList.js's ThreadLink). `s.threadCount` (rowToMessage's
// `r.thread_count`) is a server-computed SNAPSHOT taken whenever the message
// was fetched — sending a thread reply (ThreadPanel.js -> sendMessage with
// threadRoot set) optimistically appends the reply to
// `root.messagesByChannel`, but nothing increments the PARENT row's stale
// `threadCount` field, so the pill reads 0 until the next full fetch/reload
// re-snapshots it (CHAT-COMPOSE-1 item 5). ThreadPanel's own ReplyCount
// (line ~252) never had this bug — it already derives its count live by
// filtering the current message list instead of trusting a snapshot field.
// This mirrors that same reactive derivation for the inline pill so both
// counters share one live source of truth instead of two.
export const liveThreadCount = (s) => {
  if (!s || !s.id || !s.root) return 0
  const cid = s.channelId || s.root.activeChannelId
  if (!cid) return parseInt(s.threadCount, 10) || 0
  const list = (s.root.messagesByChannel || {})[cid] || []
  return list.filter((m) => m.threadRoot === s.id).length
}

// Resolve a thread's root message from `root.activeThreadRootId`. Moved out
// of ThreadPanel.js's module-scope `_parentMessage` closure — Rule 33, same
// class as `isMine` above.
export const parentMessage = (root) => {
  if (!root || !root.activeChannelId || !root.activeThreadRootId) return null
  const list = (root.messagesByChannel || {})[root.activeChannelId] || []
  return list.find((m) => m.id === root.activeThreadRootId) || null
}

// Is a given member online right now?
//
// Source of truth is `root.onlineUsers` — the app-wide presence list synced
// by shared/functions/presence.js (`useOnlinePresence`), subscribed once at
// shell scope in workspace/app.js so it is populated on EVERY route. This is
// the same source OnlineMembers (the AppHeader presence strip) reads, so the
// sidebar dots and the header avatars can never disagree.
//
// We deliberately do NOT read `root.presence` here: that map is only filled by
// subscribeRealtime on the /chat page, so it is empty when the sidebar renders
// inside the Messenger overlay on home/canvas. `onlineUsers` works everywhere.
//
// Email comparison is case-insensitive — presence keys echo whatever case the
// user authenticated with, while channel-member rows may store a normalised
// form (the same mismatch tickets/fetchGeneralTriage.js guards against).
export const isEmailOnline = (root, email) => {
  if (!root || !email) return false
  const target = String(email).toLowerCase()
  return (root.onlineUsers || []).some(
    (u) => u && u.email && String(u.email).toLowerCase() === target
  )
}

// Is the viewer ACTIVELY looking at this channel right now? — active channel
// + the messenger/chat surface actually in view + the tab focused. Shared by
// subscribeRealtime.js's own in-app toast (`_notifyNewMessage`, the original
// site this was extracted from — CHAT-NOTIFY-1, tickets/opus.md) and
// `subscribeNotifications.js` (workspace shell), which needs the identical
// check so it can skip its OWN generic foreground toast for a
// `payload.kind === 'chat_message'` bell row instead of stacking a second,
// redundant toast on top of the one chat's own realtime handler already
// shows. Registered (not a bare export closure) so `el.call('isChatChannelInView', …)`
// resolves it across the package boundary — see Rule 33 / eagerSlice.js.
export const isChatChannelInView = (root, channelId) => {
  if (!root || !channelId) return false
  const onChatRoute =
    stripWorkspacePrefix(
      root.currentPath || (typeof window !== 'undefined' ? window.location.pathname : '/')
    ) === '/chat'
  return (
    root.activeChannelId === channelId &&
    (root._chatDrawerOpen || onChatRoute) &&
    typeof document !== 'undefined' &&
    document.hasFocus()
  )
}

// Member count for a channel. Single source for ChatHeader's "N members"
// and the Messenger overlay header's "(N members)" pill — both filtered
// `root.channelMembers` by channel id inline (and the overlay did it twice,
// once for `hide` and once for `text`). Pass `root.activeChannelId` or any
// channel id; returns 0 when unset.
// DISTINCT members: a duplicated row (two writers racing one insert, two
// casings of one address) must not count the same person twice.
export const channelMemberCount = (root, channelId) => {
  const id = channelId ?? root?.activeChannelId
  if (!root || !id) return 0
  const seen = new Set()
  for (const m of root.channelMembers || []) {
    if (m && m.channel_id === id && m.member_email) seen.add(normEmail(m.member_email))
  }
  return seen.size
}

// ── What a conversation IS — for its header and its opening intro ──────────
// ONE read, so ChatHeader and the empty-thread intro (MessageList Empty) can
// never disagree: kind, the title WITHOUT the '# ' prefix (the glyph tile
// carries it), private flag, topic and — for a 1:1 DM — the partner (email,
// name, job title, presence). `title` may be a polyglot KEY for a nameless
// conversation (channelDisplayName's contract); render sites resolve it.
export const chatConversationInfo = (root, channelId) => {
  const id = channelId ?? root?.activeChannelId
  const ch = id ? (root?.channels || []).find((c) => c.id === id) : null
  if (!ch) return null
  const kind = ch.kind || 'channel'
  const people = _peopleByEmail(root)
  const title = channelDisplayName(ch, root.channelMembers || [], people, root.currentUserEmail, {
    firstOnly: kind === 'group_dm'
  })
  const info = {
    id,
    kind,
    title,
    isPrivate: !!(ch.is_private ?? ch.isPrivate),
    topic: String(ch.topic || '').trim()
  }
  if (kind === 'dm') {
    const me = normEmail(root.currentUserEmail)
    const other = (root.channelMembers || []).find(
      (m) => m && m.channel_id === id && normEmail(m.member_email) !== me
    )
    const email = (other && other.member_email) || null
    const person = (email && people[email]) || null
    info.partnerEmail = email
    info.partnerName = (person && person.name) || title
    info.partnerTitle = String((person && (person.title || person.jobTitle)) || '').trim()
    info.online = isEmailOnline(root, email)
  }
  return info
}

// Who is IN a channel, for the header's member button: its member rows, the
// people the settings sheet lists. Every kind counts the same way — a PUBLIC
// channel no longer counts everyone who can read it (it counted the whole
// workspace, so an add never moved the count; 2026-10-29). One count per
// human: the user id when a row carries one, else the email (a viewer who is
// not a manager gets reduced rows, without email). `channelMemberCount`
// stays the broadcast-mention guard's count.
// → { count, names: [up to `max` display names for the avatar stack] }
export const channelMemberSummary = (root, channelId, max = 3) => {
  const id = channelId ?? root?.activeChannelId
  const ch = id ? (root?.channels || []).find((c) => c.id === id) : null
  if (!ch) return { count: 0, names: [] }
  const byEmail = _peopleByEmail(root)
  const byId = new Map()
  for (const p of root.people || []) {
    const uid = p && (p.user_id || p.userId)
    if (uid && !byId.has(uid)) byId.set(uid, p)
  }
  const seen = new Set()
  const names = []
  for (const m of root.channelMembers || []) {
    if (!m || m.channel_id !== id) continue
    const uid = m.user_id || m.userId
    const email = normEmail(m.member_email)
    const key = uid ? `id:${uid}` : email
    if (!key || seen.has(key)) continue
    seen.add(key)
    const person = (m.member_email && byEmail[m.member_email]) || (uid && byId.get(uid)) || null
    const name = (person && person.name) || email
    if (names.length < max && name) names.push(name)
  }
  return { count: seen.size, names }
}

// ── Broadcast mentions (@all / @everyone) ───────────────────────────────────
// A message containing either token counts as mentioning EVERY member of its
// channel except the author. Single source of truth for the token list + the
// detection regex so Composer.js (autocomplete + send guard), sendMessage.js
// (notification fan-out), subscribeRealtime.js (per-viewer myMentions
// bookkeeping), and highlightMentions.js (rendering) can't drift apart.
//
// SERVER-SIDE MIRROR: server/src/domains/chat/store/mongoStore.js keeps its own
// copy of this token list (+ an identical detection regex) so a broadcast
// persists a ChatMention row for every channel member — without it, a member
// who was offline when the broadcast was sent never gets one. server/ cannot
// import from workspace/, so there is no shared module. KEEP BOTH IN SYNC.
export const BROADCAST_MENTION_TOKENS = ['all', 'everyone']
// A polyglot KEY — MentionDropdown resolves it at its render site.
export const BROADCAST_MENTION_CAPTION = 'chat.composer.broadcastCaption'

const _BROADCAST_MENTION_RE = new RegExp(
  `(?<=^|\\s)@(${BROADCAST_MENTION_TOKENS.join('|')})\\b`,
  'i'
)

export const isBroadcastMention = (body) => !!body && _BROADCAST_MENTION_RE.test(body)

export const getActiveWorkspaceId = (root, channel) =>
  channel?.workspaceId ||
  // The workspace the TAB is actually viewing — URL-derived (/w/<slug>),
  // written by bootShell's auth-snapshot resolution and replaced by the
  // in-app switch flow. Under the multi-tab model (MULTI_WORKSPACE_TABS.md)
  // this is the ONLY per-tab truth: both fallbacks below are process-global
  // mirrors of the SDK/claim context, which URL-based workspace browsing
  // never rotates — scoping against them stamped chat creates into (and
  // listed rows from) whatever workspace another session had active
  // (verified live 2026-07-22, /w/uhy tab creating DMs into bellforge).
  root?.workspace?.id ||
  // Mirror of sdk._context.activeWorkspaceId, written by onWorkspaceChange
  // when an in-app switch resolves. Still outranks the JWT claim: the
  // federated re-mint is async/best-effort (and can never land while the
  // federation plane is down), so jwtClaims.active_workspace_id can point
  // at the PREVIOUS workspace indefinitely — scoping against it filtered
  // the NEW workspace's channels to zero (verified live 2026-07-13,
  // aihouse → Symbols Intranet switch).
  root?.activeWorkspaceId ||
  root?.jwtClaims?.active_workspace_id ||
  root?.org?.id ||
  root?.jwtClaims?.workspace_org_id ||
  null

// ── Canonical workspace-scoped readers ──────────────────────────────────────
// EVERY consumer that lists or aggregates chat rows must read through these,
// never through raw `root.channels` / `root.channelMembers` — the server's
// bulk lists return the workspace ∪ cross-workspace-membership union (the
// chat_channels RLS `is_channel_member` disjunct), so the raw state keys can
// contain other workspaces' rows. Workspace isolation is the product
// contract (2026-07-12 per Nika "keep workspace data isolated from other
// workspaces"): one workspace's sidebar, Messenger list, unread badge, and
// people roster must never surface another workspace's records.
export const scopedChannels = (root) =>
  filterChannelsToActiveWorkspace(root?.channels || [], getActiveWorkspaceId(root))

export const scopedChannelIdSet = (root) => new Set(scopedChannels(root).map((c) => c.id))

// Scope a raw chat_channel_members row list by its own workspace_id column,
// falling back to channel scope for rows that predate the column.
export const scopeMembersToActiveWorkspace = (rows, root) => {
  const list = Array.isArray(rows) ? rows : []
  const activeWs = getActiveWorkspaceId(root)
  if (!activeWs) return list
  const chIds = scopedChannelIdSet(root)
  return list.filter((m) => {
    if (m && m.workspace_id) return m.workspace_id === activeWs
    return !m || !m.channel_id || chIds.size === 0 || chIds.has(m.channel_id)
  })
}

// True when a realtime/server row may enter the active workspace's state:
// rows carrying a workspace_id must match; rows without one fall back to
// their channel's scope (unknown channels pass — the channel event that
// defines them is itself workspace-gated, so orphans stay renderless).
export const rowInActiveWorkspace = (root, row) => {
  if (!row) return false
  const activeWs = getActiveWorkspaceId(root)
  if (!activeWs) return true
  const ws = row.workspace_id || row.workspaceId
  if (ws) return ws === activeWs
  const chId = row.channel_id || row.channelId
  if (!chId) return true
  const ch = (root?.channels || []).find((c) => c.id === chId)
  if (!ch) return true
  return !ch.workspaceId || ch.workspaceId === activeWs
}

// Build the deduped list of dm / group_dm channels the current user belongs
// to. `external: false` keeps conversations where every other participant is
// internal (in `root.people`); `external: true` keeps conversations with at
// least one external participant. Both the sidebar DM list and the External
// People list use this with different `external` flags — factored out so
// they can't drift.
export const buildDMChannels = (root, external) => {
  if (!root) return []
  const me = root.currentUserEmail
  const meNorm = normEmail(me)
  const members = root.channelMembers || []
  // Normalized so case-variant membership rows can't misclassify an internal
  // teammate as external (or split one person across two identities below).
  // Includes every person a member row names by user_id — without it a
  // non-manager (whose /people rows carry no peer email) filed EVERY DM with
  // a teammate under External People.
  const peopleEmails = new Set(Object.keys(_peopleByEmail(root)).map(normEmail).filter(Boolean))
  // Scope DMs to the active workspace too — a DM under symbols workspace
  // shouldn't appear when the personal-org is active. Prefer the drift-
  // corrected `root.workspace.id` from bootShell._resolveAuthSnapshot
  // over the raw JWT claim: `jwtClaims.active_workspace_id` lags behind
  // `sdk.setActiveOrganization` and can point at a workspace in a previous
  // org (yielding zero matches and an empty DM list). Mirrors Sidebar.js's
  // `_activeWorkspaceId` — keep these two in sync.
  const activeWs = getActiveWorkspaceId(root)
  const scopedChannels = filterChannelsToActiveWorkspace(root.channels || [], activeWs)

  // Others are DISTINCT + case-normalized (distinctOthers): membership rows
  // can duplicate one person under two casings, and the raw list made the
  // same human count as two "others" — which split one person across two
  // sidebar rows (two DM rows for the same teammate; dragging one onto the
  // other then composed a bogus "group of the duplicated person") and
  // misflagged real 1:1s as group conversations below.
  const myChannelOthers = new Map()
  for (const ch of scopedChannels) {
    if (ch.kind !== 'dm' && ch.kind !== 'group_dm') continue
    const all = members.filter((m) => m.channel_id === ch.id).map((m) => m.member_email)
    if (!all.some((e) => normEmail(e) === meNorm)) continue
    const others = distinctOthers(all, me)
    if (!others.length) continue
    myChannelOthers.set(ch.id, others)
  }

  const seenIds = new Set()
  const seenMemberKeys = new Set()
  return scopedChannels.filter((c) => {
    if (!myChannelOthers.has(c.id)) return false
    if (seenIds.has(c.id)) return false
    const others = myChannelOthers.get(c.id)
    const allInternal = others.every((e) => peopleEmails.has(e))
    if (external ? allInternal : !allInternal) return false
    const memberKey = others.slice().sort().join(',')
    if (seenMemberKeys.has(memberKey)) return false
    seenIds.add(c.id)
    seenMemberKeys.add(memberKey)
    // A true 1:1 DM has exactly ONE distinct other member. A channel labelled
    // `dm` with 2+ others is really a group conversation (stale/wrong server
    // `kind`). `ChatDMRow` only renders the FIRST other member, so several
    // such channels that share a first member (e.g. me+imad+zajim,
    // me+imad+toko) all render as the same name → visible duplicates in
    // DIRECT MESSAGES. Normalize them to group_dm so they render once each in
    // GROUPS instead. Idempotent (only dm→group_dm, never the reverse).
    if (c.kind === 'dm' && others.length > 1) c.kind = 'group_dm'
    return true
  })
}

// ── Per-channel message writes (narrow reactivity) ──────────────────────────
// `messagesByChannel` is a NESTED signal store (smbls wraps any plain-object
// state value so each key is its own signal — see signal/src/store.js). Writing
// a SINGLE channel's key mutates only that key's signal, so a message in one
// channel re-runs ONLY that channel's subscribers (its MessageList children
// factory, its one sidebar row's unread badge) — not every chat reader.
//
// Replacing the whole map instead —
//   root.update({ messagesByChannel: { ...root.messagesByChannel, [id]: next } })
// — sets the CONTAINER signal, which every reader of `root.messagesByChannel`
// (all ~N sidebar rows + the list) is subscribed to, so it re-runs ALL of them
// on ANY channel's message. That coarse fan-out is the perf bug this avoids.
//
// This is the exact mechanism the shipped team-board presence fix uses
// (`presenceById[email] = status` in shared/functions/presence.js
// syncPresenceById): mutate one nested key, fire one signal. Readers that read
// `root.messagesByChannel[channelId]` auto-subscribe to that channel's key
// (the store lazily creates the key's signal on first read, so a channel with
// no messages yet still gets woken when its first message lands).
//
// Callers run from event handlers / realtime callbacks (no active observer), so
// reading `root.messagesByChannel` here does not itself create a subscription.
//
// ⛔ `issuedFor` IS THE TENANT GUARD FOR THE ONE SCOPED KEY THAT IS NOT WRITTEN
// AS A PATCH (CHAT-LATE-WRITES-NO-TENANT-GUARD-1). `messagesByChannel` is
// workspace-scoped, so a fetch or a send issued in workspace A must not land
// its rows in workspace B after a switch. Every other chat late write routes
// through `sameWorkspaceWriter(root)`; this one cannot, because the whole point
// of the function is the nested assignment. So the caller captures
// `wsIdOf(root)` BEFORE its first `await` and passes it here, and this function
// makes the same comparison the writer makes. Returns `true` when the rows
// landed and `false` when they were dropped. Omitting `issuedFor` is allowed
// and means "no await happened" — a synchronous caller cannot outlive a switch.
// The ONE ordering rule for a channel's message list: oldest first, newest
// last. That is the order ChatMessageList renders straight down the column —
// it applies no sort of its own (components/MessageList.js `List.children`
// only filters + paginates), so whatever is in the array is what the reader
// sees.
//
// Enforced HERE, at the single write choke point, rather than asked of the
// eleven call sites that reach it (sendMessage's optimistic append and its
// reconcile, subscribeRealtime's insert, reactions, edits, deletes, the
// drawer's first load). All of them used to APPEND and trust that "last in
// the array" meant "newest in time" — a guess that breaks on clock skew
// between two senders, on a realtime row arriving after a newer one, and on
// an optimistic row stamped with the SENDER's clock rather than the server's.
// Sorting once on write makes every one of them correct at once.
//
// It is also the precondition for scroll-to-load-older (the `beforeId` cursor
// in collab/queries.js, not yet wired to anything): a page of older rows can
// be merged in with a plain concat, in any order, and still land in the right
// place. The caller never has to know whether to prepend or append, and a
// realtime message arriving mid-load cannot corrupt the sequence.
//
// `id` breaks ties, so two messages sharing a `created_at` hold a stable
// position instead of swapping on each re-sort — a swap would re-key their
// rows and churn the reconciler. Returns a NEW array: callers pass arrays
// derived from state, and an in-place sort would mutate the signal's current
// value before the write lands.
//
// ⭐ THE KEY IS A NUMBER, NOT A DATE. This runs on EVERY write — each switch,
// send, reconcile, reaction and realtime row re-sorts the whole channel — so
// the comparator reads the `createdAtTimestamp` soft column the mappers
// (rowToMessage, collab/drawer.js drawerRowToMessage, sendMessage's optimistic
// row) compute once per message. It used to build two Dates per comparison,
// i.e. ~2·n·log n of them per write. A message without the column (a cached
// or hand-built object) still sorts correctly: its key is parsed from
// `createdAt` instead, and junk reads as 0 — never NaN, which would make the
// comparator inconsistent.
export const sortMessagesByTime = (messages) => {
  const keyOf = (m) =>
    Number.isFinite(m?.createdAtTimestamp) ? m.createdAtTimestamp : Date.parse(m?.createdAt) || 0
  return (Array.isArray(messages) ? messages.slice() : []).sort(
    (a, b) => keyOf(a) - keyOf(b) || String(a?.id || '').localeCompare(String(b?.id || ''))
  )
}

export const setChannelMessages = (root, channelId, next, issuedFor) => {
  if (!root || !channelId) return false
  if (issuedFor !== undefined && wsIdOf(root) !== issuedFor) return false
  // Lazily materialise the nested store before the first per-channel write
  // (realtime can fire before the bulk fetch resolves). One container write,
  // then only per-channel mutations from here on.
  if (!root.messagesByChannel) root.update({ messagesByChannel: {} })
  // Non-arrays pass through untouched. MessageList reads `undefined` as "no
  // history read has landed for this channel yet" (LoadingBubbles) and `[]`
  // as "a read landed with no rows" (Empty) — coercing one into the other
  // here would swap the skeletons for a premature "No messages yet".
  root.messagesByChannel[channelId] = Array.isArray(next) ? sortMessagesByTime(next) : next
  return true
}

// ── History rows vs live rows (CHAT-EMPTY-CHANNEL-SKELETON) ─────────────────
// A channel's entry in `messagesByChannel` holds two kinds of row:
//   • HISTORY — read from the server: the /chat page's bulk read (the
//     workspace's newest 500), the per-channel read (functions/
//     loadChannelMessages.js), the shell's workspace-change refetch.
//   • LIVE — a row that reached the channel any other way: a realtime insert
//     (subscribeRealtime, the drawer's per-channel stream) or this client's own
//     send (sendMessage's optimistic row and its reconciled copy). Every live
//     writer stamps `_live: true`; a history read never does.
// A realtime row that lands in a channel nobody has read is NOT that channel's
// history. The drawer's old guard `(map[id] || []).length` read such an entry
// as loaded, so a quiet channel opened on its newest message alone (RACE 2 in
// __tests__/chatEmptyChannelLoads.test.js).

// The per-channel read's page size. `GET /chat/channels/:id/messages` answers
// at most this many rows, OLDEST first: the server sorts createdAt ascending
// and ignores every order option (collab/queries.js `listMessages` carries the
// full account). So a page that comes back this full is the channel's OLDEST
// page, not its newest. One number: listMessages' default reads it, and so
// does mergeChannelHistory. A registered zero-arg function, not a module
// constant, for the same Rule 33 reason as `chatPageSize` above.
export const channelHistoryPageSize = () => 200

// Has a history read landed for this channel? `undefined` (never read), `[]`
// and an entry of live rows only all answer false. `[]` answers false on
// purpose: an empty channel is read again each time it opens, which is one
// cheap request and heals a message the realtime stream missed while the
// channel sat empty in memory.
export const hasChannelHistory = (list) => Array.isArray(list) && list.some((m) => !!m && !m._live)

// Merge rows just READ FROM THE SERVER for one channel into its current entry.
// Fetched rows win on an id collision (server truth). Live rows the read does
// not hold are kept, so a realtime message or an optimistic send that landed
// while the read was in flight survives it.
//
// History rows the read does not hold are kept too, except across a gap. Each
// history source answers one CONTIGUOUS block of the channel's timeline: the
// bulk read the channel's newest window, a per-channel read its oldest page.
// Two blocks that share a message join into one block. Two blocks that share
// none can hide a gap between them, and a gap that renders as continuous
// history is worse than a shorter history. So when the OLDER block is a full
// page (channelHistoryPageSize) it is the per-channel endpoint's oldest page:
// it is dropped, and the newer block, the one the conversation shows, stays.
// A shorter older block was a COMPLETE read (the channel had fewer messages
// than one page), so it joins whatever is newer.
//
// Returns a NEW array, oldest first (sortMessagesByTime).
export const mergeChannelHistory = (current, fetched) => {
  const keyOf = (m) =>
    Number.isFinite(m?.createdAtTimestamp) ? m.createdAtTimestamp : Date.parse(m?.createdAt) || 0
  const idOf = (m) => String(m && m.id)
  const page = (Array.isArray(fetched) ? fetched : []).filter((m) => m && m.id != null)
  const cur = (Array.isArray(current) ? current : []).filter(Boolean)
  const pageIds = new Set(page.map(idOf))
  const history = cur.filter((m) => !m._live)
  let keepHistory = true
  if (page.length && history.length && !history.some((m) => pageIds.has(idOf(m)))) {
    const newest = (list) => list.reduce((n, m) => Math.max(n, keyOf(m)), -Infinity)
    const oldest = (list) => list.reduce((n, m) => Math.min(n, keyOf(m)), Infinity)
    const full = channelHistoryPageSize()
    if (newest(page) < oldest(history) && page.length >= full) return sortMessagesByTime(cur)
    if (newest(history) < oldest(page) && history.length >= full) keepHistory = false
  }
  const kept = cur.filter((m) => !pageIds.has(idOf(m)) && (keepHistory || m._live))
  return sortMessagesByTime([...page, ...kept])
}

// The /chat page's bulk read lands as ONE write of the whole container
// (`as: '~/messagesByChannel'`, pages/index.js): on its first load, on a cache
// hit, and on every focus and reconnect refetch. When that write held only the
// bulk's buckets it REPLACED the container, so every channel the bulk does not
// cover went back to undefined, i.e. the skeleton: an empty channel read on
// its own, a DM the server hides, a quiet channel, a realtime row in any of
// them (RACE 1 in __tests__/chatEmptyChannelLoads.test.js). Every key the bulk
// lacks stays as it is; every key it has merges through mergeChannelHistory,
// which also keeps the oldest-first order. Returns a fresh plain object.
export const mergeBulkMessages = (current, buckets) => {
  const cur = current && typeof current === 'object' ? current : {}
  const out = {}
  for (const k of Object.keys(cur)) out[k] = cur[k]
  for (const k of Object.keys(buckets || {})) out[k] = mergeChannelHistory(cur[k], buckets[k])
  return out
}

// Drop one channel's messages entirely (channel deleted). Deletes the nested
// key so its signal fires `undefined` for that channel's subscribers only,
// without replacing (and thus re-firing) the whole container.
// `issuedFor` behaves exactly as in setChannelMessages above.
export const deleteChannelMessages = (root, channelId, issuedFor) => {
  if (!root || !channelId || !root.messagesByChannel) return false
  if (issuedFor !== undefined && wsIdOf(root) !== issuedFor) return false
  delete root.messagesByChannel[channelId]
  return true
}

// Group consecutive messages from the same author within a 5-minute window —
// the second and subsequent ones drop the avatar/name header (Slack style).
// `opts.newFrom` = { from, until } (ISO / ms) + `opts.me`: the window of what
// was UNREAD when the reader opened the channel — `from` their last-read time
// then, `until` the moment of opening (root._chatNewFrom, captured on the
// channel switch, or by markRead on a first page load). The FIRST message by
// someone else inside it is flagged `_firstNew` — the row draws the "New"
// divider above itself — and is never grouped under the message before it, so
// its author shows under the divider. Messages that arrive while you watch
// fall after `until` and never get the line.
export const groupMessages = (messages = [], opts = {}) => {
  const FIVE_MIN = 5 * 60 * 1000
  const win = opts && opts.newFrom
  const from = win && win.from != null ? new Date(win.from).getTime() : null
  const until = win && win.until != null ? new Date(win.until).getTime() : Infinity
  const meNorm = opts && opts.me ? normEmail(opts.me) : null
  let marked = false
  let lastAuthor = null
  let lastTime = 0
  let lastDay = null
  return messages.map((m) => {
    const t = new Date(m.createdAt).getTime()
    const day = localDate(new Date(m.createdAt))
    const newDay = day !== lastDay
    const firstNew =
      !marked && from != null && t > from && t <= until && normEmail(m.authorEmail) !== meNorm
    if (firstNew) marked = true
    const grouped = !newDay && !firstNew && m.authorEmail === lastAuthor && t - lastTime < FIVE_MIN
    lastAuthor = m.authorEmail
    lastTime = t
    lastDay = day
    return { ...m, _grouped: grouped, _newDay: newDay, _day: day, _firstNew: firstNew }
  })
}
